import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

/**
 * Toast Context for managing toast notifications
 */
const ToastContext = createContext(null);

/**
 * Toast Provider Component
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, variant = 'info', duration = 3000) => {
    const id = Date.now() + Math.random();
    const toast = { id, message, variant, duration };
    
    setToasts((prev) => [...prev, toast]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

/**
 * Hook to use toast notifications
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

/**
 * Toast Container Component
 */
const ToastContainer = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return (
    <div 
      className="fixed top-4 right-4 z-50 flex flex-col gap-2"
      style={{ 
        position: 'fixed',
        top: '1rem',
        right: '1rem',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        maxWidth: '420px',
        width: '100%',
        pointerEvents: 'none'
      }}
    >
      {toasts.map((toast) => (
        <Toast 
          key={toast.id} 
          toast={toast} 
          onRemove={() => onRemove(toast.id)} 
        />
      ))}
    </div>
  );
};

/**
 * Individual Toast Component
 */
const Toast = ({ toast, onRemove }) => {
  const { message, variant } = toast;

  const getVariantStyles = () => {
    switch (variant) {
      case 'success':
        return {
          bg: '#10b981',
          border: '#059669',
          icon: CheckCircle2,
          color: '#ffffff'
        };
      case 'error':
        return {
          bg: '#ef4444',
          border: '#dc2626',
          icon: AlertCircle,
          color: '#ffffff'
        };
      case 'info':
      default:
        return {
          bg: '#3b82f6',
          border: '#2563eb',
          icon: Info,
          color: '#ffffff'
        };
    }
  };

  const styles = getVariantStyles();
  const Icon = styles.icon;

  return (
    <div
      className="toast-notification"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.875rem 1rem',
        backgroundColor: styles.bg,
        border: `1px solid ${styles.border}`,
        borderRadius: '0.5rem',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        color: styles.color,
        fontSize: '0.875rem',
        fontWeight: '500',
        pointerEvents: 'auto',
        animation: 'slideInRight 0.3s ease-out',
        minWidth: '320px'
      }}
      role="alert"
      aria-live="polite"
    >
      <Icon className="h-5 w-5 flex-shrink-0" style={{ width: '1.25rem', height: '1.25rem' }} />
      <span style={{ flex: 1 }}>{message}</span>
      <button
        onClick={onRemove}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0.25rem',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          opacity: 0.8,
          transition: 'opacity 0.2s',
          color: styles.color
        }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
        aria-label="Close notification"
      >
        <X className="h-4 w-4" style={{ width: '1rem', height: '1rem' }} />
      </button>
    </div>
  );
};

// Add animation styles
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideInRight {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }
  `;
  document.head.appendChild(style);
}

export default Toast;
